/**
 * useDerivedApplicationSeason — web client hook for the
 * apply-season-auto-derivation applicant flow.
 *
 * This is the thin I/O adapter over the pure `deriveApplicationSeason(seasons,
 * now)` core (`infrastructure/lambda/pageant-year/seasonResolvers.ts`). It
 * answers the applicant's "which single Season is my application scoped to right
 * now?" question by loading every Season from the Season SSOT (`dev-PageantYear`)
 * via the SAME cached `listSeasons` data path that backs
 * `useActiveIntakeSeasons` / `useSeasons`, then deriving exactly one
 * `Derived_Application_Season` for the current instant.
 *
 * Design contract (see `.kiro/specs/apply-season-auto-derivation/design.md`,
 * "Components and Interfaces → 3. useDerivedApplicationSeason"):
 *   - Loads Seasons from `dev-PageantYear` (reusing the shared `useSeasons()`
 *     cached `listSeasons` query — the same read `useActiveIntakeSeasons` uses).
 *   - Returns `deriveApplicationSeason(seasons, now)` mapped to
 *     `{ season, competitionYear, status, refetch }`. The clock is injected at
 *     THIS I/O boundary; the pure core never calls `new Date()`.
 *   - Fail-closed (R4.3): a failed or empty Season read yields `unresolved`.
 *     `useSeasons()` already normalizes both a rejected `listSeasons` query and a
 *     null/empty result to `[]`; `deriveApplicationSeason([], now)` is
 *     `{ kind: 'unresolved' }`, so the fail-closed behavior falls out of reusing
 *     it. Callers then treat the result as "no open application window".
 *
 * Recompute-on-resolution, not live re-render: `now` is read at evaluation time.
 * A season opening mid-session becomes reachable on the applicant's next
 * resolution (next mount / `refetch()`).
 *
 * Requirements: 2.1, 2.3, 4.3 (apply-season-auto-derivation)
 */
import { useMemo } from 'react';

import { deriveApplicationSeason } from '../../../infrastructure/lambda/pageant-year/seasonResolvers';

import {
  useSeasons,
  type Season,
  type SeasonResolutionStatus,
} from '@/hooks/useActiveSeason';

/** Shape returned by `useDerivedApplicationSeason`. */
export interface UseDerivedApplicationSeasonResult {
  /**
   * The single `Derived_Application_Season` at evaluation time, or `null` while
   * loading or when unresolved (no open window, empty list, or a failed read —
   * fail-closed, R4.3).
   */
  season: Season | null;
  /**
   * `season.competitionYear` when resolved; `null` while loading or when
   * unresolved.
   */
  competitionYear: number | null;
  /**
   * `loading` while the underlying Season read is in flight; `resolved` when a
   * Derived_Application_Season exists; `unresolved` when none can be derived (no
   * open window, empty list, or a failed read).
   */
  status: SeasonResolutionStatus;
  /** Bust the season-list cache and re-run the query. */
  refetch: () => void;
}

/**
 * Resolve the single `Derived_Application_Season` at `now`.
 *
 * @param now Optional instant to evaluate intake windows against. Defaults to
 *            the current time (`new Date()`), injected here at the I/O boundary.
 *            Pass an explicit `Date` for deterministic tests.
 */
export function useDerivedApplicationSeason(
  now?: Date,
): UseDerivedApplicationSeasonResult {
  const { seasons: allSeasons, status: listStatus, refetch } = useSeasons();

  // Snapshot the explicit `now` (if any) into a primitive so the memo key is
  // stable; when `now` is undefined we evaluate against the current instant.
  const nowMs = now ? now.getTime() : undefined;

  // `deriveApplicationSeason` selects exactly one of the Season objects it is
  // given (or none). Its parameter/return use the infra `Season` type (whose
  // scheduling windows are optional), so we cast the resolved season back to the
  // structurally-narrower FE `Season` — it is exactly one of the FE seasons
  // passed in, which always carry `intakeWindow` / `judgingWindow`.
  const season = useMemo<Season | null>(() => {
    const resolution = deriveApplicationSeason(
      allSeasons,
      nowMs !== undefined ? new Date(nowMs) : new Date(),
    );
    return resolution.kind === 'resolved' ? (resolution.season as Season) : null;
  }, [allSeasons, nowMs]);

  const status: SeasonResolutionStatus =
    listStatus === 'loading' ? 'loading' : season ? 'resolved' : 'unresolved';

  return {
    season,
    competitionYear: season ? season.competitionYear : null,
    status,
    refetch,
  };
}
